import { Step5AresProps, Step5FormProps, WizzardStep5Form } from '../types'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useCallback } from 'react'
import { DefaultValue } from '../constants'
import useActivationObject from '../../../providers/ActivationObjectProvider/hooks/useActivationObject'
import { ActivationObject } from '../../../providers/ActivationObjectProvider/types'
import { VALIDATIONPATTERN_EMAIL, VALIDATION_REQUIRED } from '../../../../constants/validation'

import moment from 'moment'

const useStep5Form = (ares: Step5AresProps, activationObject?: ActivationObject): Step5FormProps => {
    const propName = (name: keyof WizzardStep5Form): string => name
    const handoverKeysDateTime = activationObject?.handoverKeysDateTime

    const validationSchema = React.useMemo(
        () =>
            yup.object().shape({
                [propName('startProtection')]: yup
                    .date()
                    .test('startProtection', 'Datum musí být po termínu prohlídky', function (value) {
                        const handoverDate = moment(handoverKeysDateTime, 'YYYY-MM-DD HH:mm').toDate()
                        return !value || !handoverKeysDateTime || moment(handoverDate).isBefore(moment(value))
                    }),
                [propName('endFreeProtection')]: yup.date().nullable().required(VALIDATION_REQUIRED),
                // Temporarily hidden with changed requirement for customer usecase
                // [propName('serviceFuture')]: yup.string().required(VALIDATION_REQUIRED),
                [propName('serviceFuture')]: yup.string().notRequired(),
                [propName('ico')]:
                    activationObject?.personType === 'Legal'
                        ? yup
                              .string()
                              .required(VALIDATION_REQUIRED)
                              .matches(/^[0-9]+$/, 'Pole může obsahovat pouze čísla.')
                              .min(8, 'Pole musí obsahovat prěsně 8 znaků')
                              .max(8, 'Pole musí obsahovat prěsně 8 znaků')
                              .test('aresloaded', 'Nenalezna odpovídající společnost v registru ARES', (value, context) => {
                                  return !ares.isLoading && ares.companyData !== undefined && ares.companyData.ic === value
                              })
                        : yup.string(),
                [propName('vat')]:
                    activationObject?.personType === 'Legal'
                        ? yup
                              .string()

                              .matches(/^$|^CZ[0-9]{8,10}$/, 'Povolený formát CZ a 8-10 číslic')
                        : yup.string(),
                [propName('invoiceAddressName')]: yup.string().required(VALIDATION_REQUIRED),
                [propName('invoiceAddressZIP')]: yup
                    .string()
                    .required(VALIDATION_REQUIRED)
                    .matches(/^[0-9]/, 'Pole může obsahovat pouze čísla')
                    .min(5, 'Pole musí obsahovat 5 znaků')
                    .max(5, 'Pole musí obsahovat 5 znaků'),
                [propName('invoiceAddressCity')]: yup.string().required(VALIDATION_REQUIRED),
                [propName('invoiceAddressStreet')]: yup.string().required(VALIDATION_REQUIRED),
                [propName('invoiceAddressCodeNumber')]: yup
                    .string()
                    .required(VALIDATION_REQUIRED)
                    .matches(/^[0-9]+$/, 'Pole může obsahovat pouze čísla.'),
                [propName('invoiceAddressGuidanceNumber')]: yup.string(),
                [propName('emailInvoice')]: yup
                    .string()
                    .required(VALIDATION_REQUIRED)
                    .email('Špatný formát emailové adresy')
                    .matches(VALIDATIONPATTERN_EMAIL, 'Špatný formát emailové adresy'),
                [propName('emailSendingDocumentation')]: yup
                    .string()
                    .required(VALIDATION_REQUIRED)
                    .email('Špatný formát e-mailu'),
            }),
        [activationObject?.personType, ares.companyData, ares.isLoading, handoverKeysDateTime]
    )

    const form = useForm<WizzardStep5Form>({
        criteriaMode: 'all',
        reValidateMode: 'onSubmit',
        resolver: yupResolver(validationSchema),
        defaultValues: DefaultValue,
    })

    const reset = form.reset
    React.useEffect(() => {
        if (activationObject) {
            reset({
                startProtection: activationObject.startProtection ?? DefaultValue.startProtection,
                endFreeProtection: activationObject.endFreeProtection ?? DefaultValue.endFreeProtection,
                serviceFuture: activationObject.serviceFuture ?? DefaultValue.serviceFuture,
                ico: activationObject.ico ?? DefaultValue.ico,
                vat: activationObject.vat ?? DefaultValue.vat,
                emailInvoice: activationObject.emailInvoice ?? activationObject.personEmail ?? DefaultValue.emailInvoice,
                emailSendingDocumentation: activationObject.emailSendingDocumentation ?? DefaultValue.emailSendingDocumentation,
                invoiceAddressName: activationObject.invoiceAddressName ?? DefaultValue.invoiceAddressName,
                invoiceAddressZIP: activationObject.invoiceAddressZIP ?? DefaultValue.invoiceAddressZIP,
                invoiceAddressCity: activationObject.invoiceAddressCity ?? DefaultValue.invoiceAddressCity,
                invoiceAddressStreet: activationObject.invoiceAddressStreet ?? DefaultValue.invoiceAddressStreet,
                invoiceAddressGuidanceNumber:
                    activationObject.invoiceAddressGuidanceNumber ?? DefaultValue.invoiceAddressGuidanceNumber,
                invoiceAddressCodeNumber: activationObject.invoiceAddressCodeNumber ?? DefaultValue.invoiceAddressCodeNumber,
            })
        }
    }, [activationObject, reset])

    const { update } = useActivationObject()
    const onSubmit = useCallback(
        (data: WizzardStep5Form) => {
            if (activationObject) {
                const newObject: ActivationObject = {
                    ...activationObject,
                    ...data,
                    wizardStep: 'Summary',
                }
                update(newObject)
            }
        },
        [activationObject, update]
    )

    const { setValue } = form
    const handleUseObjectAddress = useCallback(() => {
        if (activationObject) {
            setValue('invoiceAddressName', activationObject.personName || '')
            setValue('invoiceAddressZIP', activationObject.objectZIP || '')
            setValue('invoiceAddressCity', activationObject.objectCity || '')
            setValue('invoiceAddressStreet', activationObject.objectStreet || '')
            setValue('invoiceAddressCodeNumber', activationObject.objectCodeNumber || '')
            setValue('invoiceAddressGuidanceNumber', activationObject.objectGuidanceNumber || '')
        }
    }, [activationObject, setValue])

    return {
        form,
        onSubmit,
        handleUseObjectAddress,
    }
}

export default useStep5Form
